import {
  Alert,
  Box,
  Card,
  Grid,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import {
  GenerateReportMutationVariables,
  GetReportJobCostDetailMutation,
  ReportType,
} from "generated/graphql";
import useGetReportJobCostDetail from "hooks/organization/reports/types/useGetReportJobCostDetail";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import JobOptions from "modules/jobs/JobOptions";
import { kebabCase } from "lodash";
import { useSearchParams } from "react-router-dom";

const reportFilterSchema = z.object({
  jobId: z.string().min(1, "Job is required"),
});

type ReportFilterFormData = z.infer<typeof reportFilterSchema>;
type JobCostDetailPayload = GetReportJobCostDetailMutation["getReportJobCostDetail"];
type EmployeeRow = JobCostDetailPayload["employees"][number];
type EquipmentRow = JobCostDetailPayload["equipment"][number];
type MaterialRow = JobCostDetailPayload["materials"][number];
type TruckingRow = JobCostDetailPayload["trucking"][number];
type AdditionalCostRow = JobCostDetailPayload["additional_costs"][number];

const cardHeaderIconSx = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: "info.main",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

type SectionTableColumn<T> = {
  key: keyof T;
  label: string;
  align?: "left" | "right";
  width?: string;
  render?: (value: T[keyof T], row: T) => string;
};

function SectionTable<T extends { cost: number }>({
  title,
  subtitle,
  rows,
  subtotal,
  columns,
  emptyMessage,
}: {
  title: string;
  subtitle?: string;
  rows: T[];
  subtotal: number;
  columns: SectionTableColumn<T>[];
  emptyMessage: string;
}) {
  return (
    <Card>
      <MDBox p={1.5}>
        <MDTypography variant="button" fontWeight="medium" textTransform="uppercase">
          {title}
        </MDTypography>
        {subtitle ? (
          <MDTypography variant="caption" color="text" mt={0.25} mb={1} display="block">
            {subtitle}
          </MDTypography>
        ) : null}

        <TableContainer>
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              width: "100%",
              "& .MuiTableRow-root:last-of-type td": {
                borderBottom: 0,
              },
            }}
          >
            <TableHead sx={tableHeadResetSx}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    align={column.align ?? "left"}
                    sx={{
                      ...compactHeaderCellSx,
                      width: column.width,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={compactTableCellSx}>
                    <MDTypography variant="caption" color="text">
                      {emptyMessage}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow key={`${title}-${index}`}>
                    {columns.map((column) => {
                      const value = row[column.key];
                      const text = column.render ? column.render(value, row) : String(value ?? "");

                      return (
                        <TableCell
                          key={String(column.key)}
                          align={column.align ?? "left"}
                          sx={compactTableCellSx}
                        >
                          {text}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
              <TableRow>
                <TableCell
                  colSpan={columns.length - 1}
                  sx={{ ...compactHeaderCellSx, borderTop: "1px solid rgba(0,0,0,0.08)" }}
                >
                  {title} Subtotal
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ ...compactHeaderCellSx, borderTop: "1px solid rgba(0,0,0,0.08)" }}
                >
                  {formatCurrency(subtotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    </Card>
  );
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) {
    return "";
  }

  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US");
};

const formatSignedPercent = (value: number) =>
  `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(value || 0)}%`;

const formatJobFilterLabel = (jobName?: string, externalJobId?: string) => {
  const trimmedJobName = jobName?.trim();
  const trimmedExternalJobId = externalJobId?.trim();

  if (trimmedJobName && trimmedExternalJobId) {
    return `${trimmedJobName} - ${trimmedExternalJobId}`;
  }

  return trimmedJobName || trimmedExternalJobId || "";
};

const summaryColumnSx = {
  whiteSpace: "nowrap",
};

const tableHeadResetSx = {
  display: "table-header-group",
  padding: 0,
  borderRadius: 0,
};

const compactTableCellSx = {
  whiteSpace: "nowrap",
  py: 0.5,
  px: 0.75,
  fontSize: "0.85rem",
  lineHeight: 1.15,
};

const compactHeaderCellSx = {
  ...compactTableCellSx,
  fontWeight: 700,
  fontSize: "0.78rem",
  letterSpacing: "0.01em",
};

const ReportJobCostDetail = () => {
  const [searchParams] = useSearchParams();
  const methods = useForm<ReportFilterFormData>({
    resolver: zodResolver(reportFilterSchema),
    defaultValues: {
      jobId: "",
    },
  });

  const [selectedJobName, setSelectedJobName] = useState("");
  const [lastRunJobId, setLastRunJobId] = useState("");
  const [fetchReport, { data, loading, error }] = useGetReportJobCostDetail(false);
  const [generateReport, { data: generateReportData }] = useGenerateReport();
  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);
  const downloadFileRef = useRef<HTMLAnchorElement | null>(null);
  const deepLinkedJobId = searchParams.get("jobId") ?? "";

  useEffect(() => {
    if (!generateReportData?.generateReport || !downloadFileRef.current || !requestData) {
      return;
    }

    downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
      generateReportData.generateReport
    )}`;
    downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
    downloadFileRef.current.click();
  }, [generateReportData, requestData]);

  useEffect(() => {
    if (!deepLinkedJobId) {
      return;
    }

    methods.setValue("jobId", deepLinkedJobId, {
      shouldValidate: true,
      shouldDirty: false,
    });

    if (!selectedJobName) {
      setSelectedJobName(`Job #${deepLinkedJobId}`);
    }

    if (lastRunJobId === deepLinkedJobId) {
      return;
    }

    fetchReport({ variables: { jobId: deepLinkedJobId } });
    setLastRunJobId(deepLinkedJobId);
  }, [deepLinkedJobId, fetchReport, lastRunJobId, methods, selectedJobName]);

  const report = data?.getReportJobCostDetail ?? null;
  const hasReport = Boolean(report) && lastRunJobId === methods.watch("jobId");

  useEffect(() => {
    if (!report?.header.job_name) {
      return;
    }

    setSelectedJobName(
      formatJobFilterLabel(report.header.job_name, report.header.job_external_id) ||
        report.header.job_name
    );
  }, [report]);

  const summaryRows = useMemo(
    () =>
      report
        ? [
            {
              label: "Employees",
              total: report.summary.employees_total,
              proposed: report.summary.proposed_employees_total,
              difference: report.summary.employees_difference_percent,
            },
            {
              label: "Equipment",
              total: report.summary.equipment_total,
              proposed: report.summary.proposed_equipment_total,
              difference: report.summary.equipment_difference_percent,
            },
            {
              label: "Material",
              total: report.summary.material_total,
              proposed: report.summary.proposed_material_total,
              difference: report.summary.material_difference_percent,
            },
            {
              label: "Trucking",
              total: report.summary.trucking_total,
              proposed: report.summary.proposed_trucking_total,
              difference: report.summary.trucking_difference_percent,
            },
            {
              label: "Additional Costs",
              total: report.summary.additional_costs_total,
              proposed: report.summary.proposed_additional_costs_total,
              difference: report.summary.additional_costs_difference_percent,
            },
            {
              label: "TOTAL JOB COST",
              total: report.summary.total_job_cost,
              proposed: report.summary.proposed_total_job_cost,
              difference: report.summary.total_job_cost_difference_percent,
              isTotal: true,
            },
            {
              label: "TOTAL JOB PROFIT",
              total: report.summary.actual_profit,
              proposed: report.summary.proposed_profit,
              difference: report.summary.profit_difference_percent,
              isTotal: true,
            },
          ]
        : [],
    [report]
  );

  const onSubmit = async ({ jobId }: ReportFilterFormData) => {
    await fetchReport({ variables: { jobId } });
    setLastRunJobId(jobId);
  };

  const handleDownload = () => {
    const jobId = methods.getValues("jobId");
    if (!jobId) {
      return;
    }

    const variables = {
      type: ReportType.JOB_COST_DETAIL,
      jobId,
    };

    setRequestData(variables);
    generateReport({ variables });
  };

  return (
    <Box
      sx={{
        "& .css-1h9h5al-MuiAutocomplete-root .MuiOutlinedInput-root": {
          padding: "5px !important",
        },
      }}
    >
      <DashboardPage>
        <FormProvider {...methods}>
          <MDBox mb={2} component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={1.5} alignItems="center" justifyContent="center">
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    p={1.5}
                    lineHeight={1}
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Box>
                      <MDTypography variant="h5" fontWeight="medium">
                        Job Cost Detail Report
                      </MDTypography>
                      <MDTypography variant="caption" color="text" mt={0.25} display="block">
                        Review every cost on one job in a single customer-ready report
                      </MDTypography>
                    </Box>
                    <Box sx={cardHeaderIconSx}>
                      <Icon sx={{ color: "#fff", fontSize: 20 }}>receipt_long</Icon>
                    </Box>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <MDBox p={1.5}>
                    <MDTypography variant="button" mb={1} display="block">
                      Filter Options
                    </MDTypography>
                    <Grid container spacing={1} alignItems="flex-end">
                      <Grid item xs={12} md={6} lg={5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <JobOptions
                            selected={
                              methods.watch("jobId")
                                ? {
                                    label: selectedJobName || `Job #${methods.watch("jobId")}`,
                                    value: methods.watch("jobId"),
                                  }
                                : undefined
                            }
                          >
                            {({ jobOptions, loading: jobsLoading, search }) => (
                              <Controller
                                name="jobId"
                                control={methods.control}
                                render={({ field, fieldState: { error: fieldError } }) => {
                                  const selectedOption = jobOptions?.find(
                                    (x) => x.value === field.value
                                  );
                                  const selectedValue = field.value
                                    ? {
                                        value: field.value,
                                        label:
                                          selectedJobName ||
                                          selectedOption?.label ||
                                          `Job ${field.value}`,
                                      }
                                    : null;

                                  return (
                                    <ScrollAsyncAutocomplete
                                      {...field}
                                      value={selectedValue}
                                      options={jobOptions ?? []}
                                      loading={jobsLoading}
                                      label="Job"
                                      search={search}
                                      onLoadMore={() => {}}
                                      hasMore={false}
                                      error={fieldError?.message}
                                      onChange={(value) => {
                                        field.onChange(value || "");
                                        setLastRunJobId("");

                                        if (value) {
                                          const nextSelectedOption = jobOptions?.find(
                                            (x) => x.value === value
                                          );
                                          setSelectedJobName(nextSelectedOption?.label || "");
                                          return;
                                        }

                                        setSelectedJobName("");
                                      }}
                                    />
                                  );
                                }}
                              />
                            )}
                          </JobOptions>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2} lg={2}>
                        <MDBox height="100%" display="flex" flexDirection="column" justifyContent="flex-end">
                          <MDButton
                            color="success"
                            type="submit"
                            fullWidth
                            disabled={!methods.watch("jobId") || loading}
                            sx={{ height: "34px", py: 0.25, fontSize: "0.75rem" }}
                          >
                            {loading ? "Loading..." : "Run Report"}
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={1.5} lg={1.5}>
                        <MDBox height="100%" display="flex" flexDirection="column" justifyContent="flex-end">
                          <a ref={downloadFileRef} className="d-none" />
                          <MDButton
                            color="info"
                            fullWidth
                            disabled={!hasReport}
                            onClick={handleDownload}
                            sx={{ height: "34px", py: 0.25, fontSize: "0.75rem" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.4 }}>
                              download
                            </Icon>
                            Export
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>

              {error ? (
                <Grid item xs={12}>
                  <Alert severity="error">Unable to load the job cost detail report.</Alert>
                </Grid>
              ) : null}

              {!hasReport ? (
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={1.5}>
                      <MDTypography variant="button" mb={0.5} display="block">
                        Report Results
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Choose a job and run the report to view the summary and detail sections.
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Card>
                      <MDBox p={1.5}>
                        <Grid container spacing={0.75}>
                          <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <MDTypography variant="caption" color="text">
                              Job Name
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              {report?.header.job_name || selectedJobName}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <MDTypography variant="caption" color="text">
                              Customer
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              {report?.header.company_name}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <MDTypography variant="caption" color="text">
                              Job ID
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              {report?.header.job_external_id}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <MDTypography variant="caption" color="text">
                              Status
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              {report?.header.status}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} lg={2.4}>
                            <MDTypography variant="caption" color="text">
                              Total Proposed
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              {formatCurrency(report?.header.total_proposed_amount || 0)}
                            </MDTypography>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card>
                      <MDBox p={1.5}>
                        <MDTypography variant="button" mb={1} display="block">
                          Summary
                        </MDTypography>
                        <TableContainer>
                          <Table
                            size="small"
                            sx={{
                              tableLayout: "fixed",
                              width: "100%",
                              "& .MuiTableRow-root:last-of-type td": {
                                borderBottom: 0,
                              },
                            }}
                          >
                            <TableHead sx={tableHeadResetSx}>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    ...compactHeaderCellSx,
                                    width: "40%",
                                    ...summaryColumnSx,
                                  }}
                                >
                                  Category
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    ...compactHeaderCellSx,
                                    width: "20%",
                                    ...summaryColumnSx,
                                  }}
                                >
                                  Total
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    ...compactHeaderCellSx,
                                    width: "20%",
                                    ...summaryColumnSx,
                                  }}
                                >
                                  Proposed
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    ...compactHeaderCellSx,
                                    width: "20%",
                                    ...summaryColumnSx,
                                  }}
                                >
                                  Difference
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {summaryRows.map((row) => (
                                <TableRow key={row.label}>
                                  <TableCell
                                    sx={{
                                      ...compactTableCellSx,
                                      fontWeight: row.isTotal ? 700 : 400,
                                      ...summaryColumnSx,
                                    }}
                                  >
                                    {row.label}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...compactTableCellSx,
                                      fontWeight: row.isTotal ? 700 : 400,
                                      ...summaryColumnSx,
                                    }}
                                  >
                                    {formatCurrency(row.total)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...compactTableCellSx,
                                      fontWeight: row.isTotal ? 700 : 400,
                                      ...summaryColumnSx,
                                    }}
                                  >
                                    {formatCurrency(row.proposed)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...compactTableCellSx,
                                      fontWeight: row.isTotal ? 700 : 400,
                                      ...summaryColumnSx,
                                    }}
                                  >
                                    {formatSignedPercent(row.difference)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <SectionTable<EmployeeRow>
                      title="Employees"
                      rows={report?.employees || []}
                      subtotal={report?.summary.employees_total || 0}
                      emptyMessage="No employee costs were recorded for this job."
                      columns={[
                        {
                          key: "date",
                          label: "Date",
                          width: "20%",
                          render: (value) => formatDate(String(value)),
                        },
                        { key: "employee", label: "Employee", width: "30%" },
                        {
                          key: "hours",
                          label: "Hours",
                          width: "16%",
                          align: "right",
                          render: (value) => formatNumber(Number(value)),
                        },
                        {
                          key: "rate",
                          label: "Rate",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                        {
                          key: "cost",
                          label: "Cost",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <SectionTable<EquipmentRow>
                      title="Equipment"
                      subtitle="Hours are shown as quantity multiplied by recorded hours."
                      rows={report?.equipment || []}
                      subtotal={report?.summary.equipment_total || 0}
                      emptyMessage="No equipment costs were recorded for this job."
                      columns={[
                        {
                          key: "date",
                          label: "Date",
                          width: "20%",
                          render: (value) => formatDate(String(value)),
                        },
                        { key: "equipment", label: "Equipment", width: "30%" },
                        {
                          key: "hours",
                          label: "Hours",
                          width: "16%",
                          align: "right",
                          render: (value) => formatNumber(Number(value)),
                        },
                        {
                          key: "rate",
                          label: "Rate",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                        {
                          key: "cost",
                          label: "Cost",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <SectionTable<MaterialRow>
                      title="Material"
                      rows={report?.materials || []}
                      subtotal={report?.summary.material_total || 0}
                      emptyMessage="No material costs were recorded for this job."
                      columns={[
                        {
                          key: "date",
                          label: "Date",
                          width: "20%",
                          render: (value) => formatDate(String(value)),
                        },
                        { key: "material", label: "Material", width: "30%" },
                        {
                          key: "qty",
                          label: "Qty",
                          width: "16%",
                          align: "right",
                          render: (value) => formatNumber(Number(value)),
                        },
                        {
                          key: "rate",
                          label: "Rate",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                        {
                          key: "cost",
                          label: "Cost",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <SectionTable<TruckingRow>
                      title="Trucking"
                      subtitle="Qty reflects the recorded trucking quantity for the costing entry."
                      rows={report?.trucking || []}
                      subtotal={report?.summary.trucking_total || 0}
                      emptyMessage="No trucking costs were recorded for this job."
                      columns={[
                        {
                          key: "date",
                          label: "Date",
                          width: "20%",
                          render: (value) => formatDate(String(value)),
                        },
                        { key: "truck", label: "Truck", width: "30%" },
                        {
                          key: "qty",
                          label: "Qty",
                          width: "16%",
                          align: "right",
                          render: (value) => formatNumber(Number(value)),
                        },
                        {
                          key: "rate",
                          label: "Rate",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                        {
                          key: "cost",
                          label: "Cost",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <SectionTable<AdditionalCostRow>
                      title="Additional Costs"
                      rows={report?.additional_costs || []}
                      subtotal={report?.summary.additional_costs_total || 0}
                      emptyMessage="No additional costs were recorded for this job."
                      columns={[
                        {
                          key: "date",
                          label: "Date",
                          width: "20%",
                          render: (value) => formatDate(String(value)),
                        },
                        { key: "expense", label: "Expense", width: "30%" },
                        {
                          key: "qty",
                          label: "Qty",
                          width: "16%",
                          align: "right",
                          render: (value) => formatNumber(Number(value)),
                        },
                        {
                          key: "rate",
                          label: "Rate",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                        {
                          key: "cost",
                          label: "Cost",
                          width: "17%",
                          align: "right",
                          render: (value) => formatCurrency(Number(value)),
                        },
                      ]}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </MDBox>
        </FormProvider>
      </DashboardPage>
    </Box>
  );
};

export default ReportJobCostDetail;
